// Bedingungsbaum als Pillen (Agent 5A, Runde 5): Gruppen mit „alle von / eine von / nicht alle von / keine von“, Bedingungen als
// Pillen „Feld: Operator Wert“ mit ✕, „Filter hinzufügen“ und „Gruppe hinzufügen“ am Ende jeder Gruppe, verwandte Objekte
// („Hat eine Opportunity, bei der …“) als eingerückter Block. Ein Klick auf eine Pille öffnet den Editor (`FilterBedingung.tsx`).
// Alle Änderungen laufen über `aendern(fn)` des Kontexts: der Baum selbst hält keinen Zustand außer der geöffneten Pille.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { FilterKatalog, FilterKatalogFeld } from "../../api/filter";
import { Ausklapp } from "../Ausklapp";
import { Icon } from "../Icon";
import { BedingungEditor } from "./FilterBedingung";
import { FilterFeldWahl } from "./FilterFeldWahl";
import {
  ersetze, feldInfo, fuegeEin, gruppenArt, istVollstaendig, leereGruppe, mitGruppenArt, neueBedingung, neuesVerwandtes, pillenText, verknuepfer,
  GRUPPEN_ARTEN, type FeldKnoten, type GruppenArt, type Gruppe, type KatalogIndex, type Knoten, type RohKnoten, type TextKontext, type VerwandtKnoten, type VolltextKnoten,
} from "./filterModell";
import { FILTER_TEXTE, verwandtSatz } from "./filterTexte";

export interface FilterKontext {
  katalog: FilterKatalog | null;
  index: KatalogIndex | null;
  text: TextKontext;
  /** Baum ändern: die Funktion bekommt die Wurzel und gibt die neue zurück */
  aendern: (fn: (wurzel: Gruppe) => Gruppe) => void;
  /** Pille, deren Editor offen ist (neu angelegte öffnet sich selbst) */
  offenId: string | null;
  setOffenId: (id: string | null) => void;
  /** Objektart der Abfrage (Katalog-Hauptart) */
  objekt: string;
  /** nur lesen: der Katalog ließ sich nicht laden (die Pillen bleiben sichtbar, bearbeiten geht nicht) */
  nurLesen: boolean;
}

const Kontext = createContext<FilterKontext | null>(null);
export const FilterKontextProvider = Kontext.Provider;

function useKontext(): FilterKontext {
  const k = useContext(Kontext);
  if (!k) throw new Error("FilterBaum ohne FilterKontextProvider");
  return k;
}

const OBJEKT_NAMEN: Record<string, string> = { lead: "Lead", contact: "Kontakt", opportunity: "Opportunity" };

// ---------------------------------------------------------------------------------------------------------------------------------
// Pille
// ---------------------------------------------------------------------------------------------------------------------------------

function Pille({ k, ebene }: { k: FeldKnoten | VolltextKnoten; ebene: string }) {
  const ctx = useKontext();
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const offen = ctx.offenId === k.id;
  const text = pillenText(k, ebene, ctx.text);
  const unvollstaendig = !istVollstaendig(k);
  const info = k.typ === "feld" ? feldInfo(ctx.index, ebene, k.feld) : undefined;
  const entfernen = () => {
    ctx.setOffenId(null);
    ctx.aendern((w) => ersetze(w, k.id, () => null));
  };
  return (
    <span className={`fb-pille${unvollstaendig ? " fb-pille--leer" : ""}${offen ? " fb-pille--offen" : ""}`}>
      <button
        ref={setAnker}
        type="button"
        className="fb-pille__text"
        aria-haspopup="dialog"
        aria-expanded={offen}
        title={unvollstaendig ? FILTER_TEXTE.unvollstaendig : `${text.feld}: ${text.op} ${text.wert}`.replace(/\s+/g, " ")}
        disabled={ctx.nurLesen}
        onClick={() => ctx.setOffenId(offen ? null : k.id)}
      >
        <span className="fb-pille__feld">{text.feld}:</span>
        {text.op ? <span className="fb-pille__op">{text.op}</span> : null}
        <span className="fb-pille__wert">{text.wert}</span>
      </button>
      {ctx.nurLesen ? null : (
        <button type="button" className="fb-pille__x" aria-label={FILTER_TEXTE.bedingungEntfernen} title={FILTER_TEXTE.bedingungEntfernen} onClick={entfernen}>
          <Icon name="schliessen" groesse={12} />
        </button>
      )}
      <Ausklapp anker={anker} offen={offen && !ctx.nurLesen} onSchliessen={() => ctx.setOffenId(null)} breite={400} minBreite={320} maxHoehe={520} rolle="dialog" className="fe-ausklapp">
        <BedingungEditor
          knoten={k}
          info={info}
          feldName={text.feld}
          onAendern={(neu) => ctx.aendern((w) => ersetze(w, k.id, () => neu))}
          onEntfernen={entfernen}
          onFertig={() => ctx.setOffenId(null)}
        />
      </Ausklapp>
    </span>
  );
}

function RohPille({ k }: { k: RohKnoten }) {
  const ctx = useKontext();
  return (
    <span className="fb-pille fb-pille--roh" title={`${FILTER_TEXTE.unbekannteBedingung}\n${JSON.stringify(k.roh)}`}>
      <span className="fb-pille__text fb-pille__text--roh">
        <span className="fb-pille__feld">{FILTER_TEXTE.unbekannterKnoten}</span>
      </span>
      {ctx.nurLesen ? null : (
        <button type="button" className="fb-pille__x" aria-label={FILTER_TEXTE.bedingungEntfernen} title={FILTER_TEXTE.bedingungEntfernen} onClick={() => ctx.aendern((w) => ersetze(w, k.id, () => null))}>
          <Icon name="schliessen" groesse={12} />
        </button>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Hinzufügen
// ---------------------------------------------------------------------------------------------------------------------------------

function HinzuKnopf({ gruppe, ebene, automatisch, onAutoVerbraucht }: { gruppe: Gruppe; ebene: string; automatisch?: boolean; onAutoVerbraucht?: () => void }) {
  const ctx = useKontext();
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [offen, setOffen] = useState(false);
  // von außen angestoßen („Filter hinzufügen“ in der eingeklappten Zeile): Feldwahl öffnen, danach den Auftrag zurückgeben
  useEffect(() => {
    if (!automatisch) return;
    setOffen(true);
    onAutoVerbraucht?.();
  }, [automatisch, onAutoVerbraucht]);
  const hauptart = ebene === ctx.objekt;
  const kategorien = hauptart ? ctx.katalog?.kategorien : ctx.katalog?.verwandt.find((v) => v.objekt === ebene)?.kategorien;
  const verwandt = hauptart ? (ctx.katalog?.verwandt ?? []).map((v) => ({ id: v.id, label: v.label })) : [];
  const hinzufuegen = (f: FilterKatalogFeld) => {
    const knoten = neueBedingung(f);
    setOffen(false);
    ctx.aendern((w) => fuegeEin(w, gruppe.id, knoten));
    ctx.setOffenId(knoten.id);
  };
  const verwandtHinzufuegen = (id: string) => {
    setOffen(false);
    ctx.aendern((w) => fuegeEin(w, gruppe.id, neuesVerwandtes(ctx.objekt, id)));
  };
  return (
    <>
      <button ref={setAnker} type="button" className="fb-hinzu" aria-haspopup="dialog" aria-expanded={offen} disabled={!kategorien} onClick={() => setOffen((o) => !o)}>
        <Icon name="plus" groesse={13} />
        {FILTER_TEXTE.filterHinzufuegen}
      </button>
      <Ausklapp anker={anker} offen={offen} onSchliessen={() => setOffen(false)} breite={340} maxHoehe={440} rolle="dialog" className="fw-ausklapp">
        {kategorien ? <FilterFeldWahl kategorien={kategorien} verwandt={verwandt} onFeld={hinzufuegen} onVerwandt={verwandtHinzufuegen} /> : null}
      </Ausklapp>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Gruppe
// ---------------------------------------------------------------------------------------------------------------------------------

function GruppenWahl({ g }: { g: Gruppe }) {
  const ctx = useKontext();
  const art = gruppenArt(g);
  return (
    <select
      className="fb-gruppenwahl"
      value={art}
      aria-label="Wie die Bedingungen dieser Gruppe zusammenwirken"
      disabled={ctx.nurLesen}
      onChange={(e) => ctx.aendern((w) => ersetze(w, g.id, (k) => (k.typ === "gruppe" ? mitGruppenArt(k, e.target.value as GruppenArt) : k)))}
    >
      {GRUPPEN_ARTEN.map((a) => (
        <option key={a.wert} value={a.wert}>
          {a.kurz}
        </option>
      ))}
    </select>
  );
}

function Verknuepfer({ g }: { g: Gruppe }) {
  return <span className="fb-tag">{verknuepfer(g)}</span>;
}

function Block({ k, ebene, g }: { k: Gruppe | VerwandtKnoten; ebene: string; g: Gruppe }): ReactNode {
  return (
    <div className="fb-block">
      <Verknuepfer g={g} />
      {k.typ === "gruppe" ? <GruppeAnsicht g={k} ebene={ebene} /> : <VerwandtAnsicht v={k} />}
    </div>
  );
}

export function GruppeAnsicht({ g, ebene, wurzel = false, titel, automatisch, onAutoVerbraucht, ohneKopf = false }: { g: Gruppe; ebene: string; wurzel?: boolean; titel?: ReactNode; automatisch?: boolean; onAutoVerbraucht?: () => void; ohneKopf?: boolean }) {
  const ctx = useKontext();
  // aufeinanderfolgende Bedingungen stehen in einer Zeile, Gruppen und verwandte Objekte als eigene Blöcke
  const zeilen: ReactNode[] = [];
  let pillen: ReactNode[] = [];
  const spuelen = () => {
    if (pillen.length) zeilen.push(<div key={`z${zeilen.length}`} className="fb-pillen">{pillen}</div>);
    pillen = [];
  };
  g.kinder.forEach((k: Knoten, i) => {
    if (k.typ === "gruppe" || k.typ === "verwandt") {
      spuelen();
      zeilen.push(<Block key={k.id} k={k} ebene={ebene} g={g} />);
      return;
    }
    if (pillen.length || i > 0) pillen.push(<Verknuepfer key={`v${k.id}`} g={g} />);
    pillen.push(k.typ === "roh" ? <RohPille key={k.id} k={k} /> : <Pille key={k.id} k={k} ebene={ebene} />);
  });
  spuelen();
  return (
    <div className={`fb-gruppe${wurzel ? " fb-gruppe--wurzel" : ""}`}>
      {ohneKopf ? null : (
        <div className="fb-kopf">
          {titel ? <span className="fb-kopf__titel">{titel}</span> : null}
          <GruppenWahl g={g} />
          {wurzel || ctx.nurLesen ? null : (
            <button type="button" className="fb-x" aria-label={FILTER_TEXTE.gruppeEntfernen} title={FILTER_TEXTE.gruppeEntfernen} onClick={() => ctx.aendern((w) => ersetze(w, g.id, () => null))}>
              <Icon name="schliessen" groesse={12} />
            </button>
          )}
        </div>
      )}
      <div className="fb-inhalt">
        {zeilen}
        {ctx.nurLesen ? null : (
          <div className="fb-fuss">
            {g.kinder.length ? <Verknuepfer g={g} /> : null}
            <HinzuKnopf gruppe={g} ebene={ebene} automatisch={automatisch} onAutoVerbraucht={onAutoVerbraucht} />
            {ebene === ctx.objekt ? (
              <button type="button" className="fb-hinzu" onClick={() => ctx.aendern((w) => fuegeEin(w, g.id, leereGruppe(true)))}>
                <Icon name="filter" groesse={13} />
                {FILTER_TEXTE.gruppeHinzufuegen}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function VerwandtAnsicht({ v }: { v: VerwandtKnoten }) {
  const ctx = useKontext();
  const label = ctx.katalog?.verwandt.find((x) => x.objekt === v.verwandt)?.label ?? OBJEKT_NAMEN[v.verwandt] ?? v.verwandt;
  return (
    <div className="fb-verwandt">
      <div className="fb-kopf">
        <select
          className="fb-gruppenwahl fb-gruppenwahl--satz"
          value={v.negate ? "nein" : "ja"}
          aria-label="Verknüpftes Objekt"
          disabled={ctx.nurLesen}
          onChange={(e) => ctx.aendern((w) => ersetze(w, v.id, (k) => (k.typ === "verwandt" ? { ...k, negate: e.target.value === "nein" } : k)))}
        >
          <option value="ja">{verwandtSatz(label, false)}</option>
          <option value="nein">{verwandtSatz(label, true)}</option>
        </select>
        <GruppenWahl g={v.innen} />
        {ctx.nurLesen ? null : (
          <button type="button" className="fb-x" aria-label={FILTER_TEXTE.gruppeEntfernen} title={FILTER_TEXTE.gruppeEntfernen} onClick={() => ctx.aendern((w) => ersetze(w, v.id, () => null))}>
            <Icon name="schliessen" groesse={12} />
          </button>
        )}
      </div>
      <GruppeAnsicht g={v.innen} ebene={v.verwandt} ohneKopf />
    </div>
  );
}

/** Der ganze Baum: Kopfzeile „Finde Leads, auf die zutrifft: [alle von ▾]“ und die Bedingungen der Wurzelgruppe. */
export function FilterBaum({ wurzel, objekt, automatisch, onAutoVerbraucht }: { wurzel: Gruppe; objekt: string; automatisch?: boolean; onAutoVerbraucht?: () => void }) {
  return (
    <GruppeAnsicht
      g={wurzel}
      ebene={objekt}
      wurzel
      titel={objekt === "lead" ? FILTER_TEXTE.findeLeads : FILTER_TEXTE.findeAndere(OBJEKT_NAMEN[objekt] ?? objekt)}
      automatisch={automatisch}
      onAutoVerbraucht={onAutoVerbraucht}
    />
  );
}
